import React from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils/cn'
import { Logo } from './Logo'
import { useTranslation } from 'react-i18next'

/* ------------------------------------------------------------------ */
/*  Footer — site footer with link columns, legal, language switcher   */
/*  See ARCHITECTURE.md §17                                            */
/* ------------------------------------------------------------------ */

interface FooterLink {
  href: string
  labelKey: string
}

const columns: { titleKey: string; links: FooterLink[] }[] = [
  {
    titleKey: 'footer.services',
    links: [
      { href: '/tjanster', labelKey: 'footer.allServices' },
      { href: '/stader', labelKey: 'footer.allCities' },
      { href: '/hur-det-fungerar', labelKey: 'nav.howItWorks' },
      { href: '/priser', labelKey: 'nav.pricing' },
      { href: '/rut-kalkylator', labelKey: 'footer.rutCalculator' },
    ],
  },
  {
    titleKey: 'footer.forProviders',
    links: [
      { href: '/for-leverantorer', labelKey: 'nav.forProviders' },
      { href: '/skapa-konto', labelKey: 'footer.registerProvider' },
      { href: '/faq', labelKey: 'footer.faq' },
    ],
  },
  {
    titleKey: 'footer.company',
    links: [
      { href: '/om-oss', labelKey: 'nav.about' },
      { href: '/blogg', labelKey: 'footer.blog' },
      { href: '/press', labelKey: 'footer.press' },
      { href: '/kontakt', labelKey: 'nav.contact' },
    ],
  },
  {
    titleKey: 'footer.legal',
    links: [
      { href: '/villkor', labelKey: 'footer.terms' },
      { href: '/integritet', labelKey: 'footer.privacy' },
      { href: '/sakerhet', labelKey: 'footer.security' },
    ],
  },
]

export function Footer() {
  const { t, i18n } = useTranslation()
  const currentYear = new Date().getFullYear()

  const toggleLanguage = () => {
    const next = i18n.language === 'sv' ? 'en' : 'sv'
    void i18n.changeLanguage(next)
  }

  return (
    <footer className="border-t border-neutral-200 bg-neutral-50 pb-[env(safe-area-inset-bottom,0px)]" role="contentinfo">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Link columns */}
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          {columns.map((col) => (
            <div key={col.titleKey}>
              <h3 className="text-sm font-semibold text-neutral-900">{t(col.titleKey)}</h3>
              <ul className="mt-3 space-y-2">
                {col.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className={cn(
                        'text-sm text-neutral-500 transition-colors hover:text-neutral-900',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                        'rounded',
                      )}
                    >
                      {t(link.labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom row */}
        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-neutral-200 pt-8 sm:flex-row">
          <div className="flex items-center gap-4">
            <Logo size="sm" showWordmark={false} />
            <p className="text-sm text-neutral-500">
              &copy; {currentYear} Hjälptorget AB. {t('footer.allRightsReserved')}
            </p>
          </div>

          {/* Language switcher */}
          <button
            type="button"
            onClick={toggleLanguage}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-neutral-500',
              'hover:bg-neutral-100 hover:text-neutral-900',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
              'transition-colors',
            )}
            aria-label={t('footer.switchLanguage')}
          >
            <span aria-hidden="true">🌐</span>
            {i18n.language === 'sv' ? 'English' : 'Svenska'}
          </button>
        </div>
      </div>
    </footer>
  )
}

Footer.displayName = 'Footer'
